/**
 * PurchaseHistory (iter168) — Compact "everything in one place" view of a
 * player's purchases, credit usage, and post-session feedback. Consumes
 * GET /api/students/{sid}/purchase-history which returns:
 *   { total_credits_remaining, packages[], credit_usage[], feedback[] }
 *
 * Visible to: student self, admin, or a coach who has written feedback for
 * the player (backend enforces privacy — this component just renders what
 * comes back and silently hides on 403).
 */
import { useEffect, useState } from "react";
import { Package, Sparkles, CalendarClock, ChevronDown, ChevronUp, AlertTriangle, Wrench, Undo2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch { return String(iso).slice(0, 10); }
};

export default function PurchaseHistory({ studentId, studentName = "" }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || (user?.permissions || []).some((p) => p === "admin" || p === "super_admin");
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState("");
  const load = () => api.get(`/students/${studentId}/purchase-history`)
    .then((r) => setData(r.data))
    .catch(() => setData({ packages: [], credit_usage: [], feedback: [], total_credits_remaining: 0 }));
  useEffect(() => { if (studentId) load(); }, [studentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const adjustCredits = async (pack) => {
    const raw = prompt(`Current sessions used: ${pack.sessions_used} / ${pack.sessions_total}\nEnter NEW sessions_used (absolute value):`, String(pack.sessions_used));
    if (raw == null) return;
    const used = parseInt(raw, 10);
    if (Number.isNaN(used) || used < 0) { toast.error("Enter a whole number ≥ 0"); return; }
    const note = prompt("Reason for adjustment?", "") || "";
    setBusy(pack.id);
    try {
      await api.post(`/admin/packages/${pack.id}/adjust`, { sessions_used: used, note });
      toast.success(`Updated · sessions_used → ${used}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Adjust failed");
    } finally { setBusy(""); }
  };

  const refundPack = async (pack) => {
    if (!confirm(`Refund this package? This will:\n· Set sessions_used to full (0 remaining)\n· Attempt a Stripe refund of £${pack.price || "?"}\n· Notify the player\n\nContinue?`)) return;
    const note = prompt("Refund reason (shown to player)?", "") || "";
    setBusy(pack.id);
    try {
      const { data } = await api.post(`/admin/packages/${pack.id}/refund`, { note });
      if (data?.refund?.refund_offline) {
        toast.warning("Marked refunded — Stripe refund not processed (no payment_intent). Chase manually in Stripe dashboard.", { duration: 10000 });
      } else {
        toast.success(`Refunded £${(data.amount_pence / 100).toFixed(2)}`);
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Refund failed");
    } finally { setBusy(""); }
  };

  if (!data) return null;
  const packs = data.packages || [];
  const usage = data.credit_usage || [];
  const feedback = data.feedback || [];
  const total = data.total_credits_remaining || 0;
  const nothingBought = packs.length === 0;
  if (nothingBought && feedback.length === 0) return null;

  return (
    <div className="card-surface p-6 fade-up space-y-4" data-testid="purchase-history-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Package className="w-5 h-5 text-[#CCFF00]" />
          <div>
            <div className="label-mono">PURCHASE & PROGRESS HISTORY</div>
            <div className="text-xs text-[#A3A3A3]">
              {total > 0
                ? <>Total credits remaining: <span className="text-[#CCFF00]">{total}</span></>
                : "No live credits — every purchase and recap in one place."}
            </div>
          </div>
        </div>
        {(packs.length + usage.length + feedback.length) > 4 && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            data-testid="purchase-history-toggle"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-wider text-[#A3A3A3] hover:text-white border border-[#262626] hover:border-[#404040]"
          >
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {expanded ? "Collapse" : "Expand all"}
          </button>
        )}
      </div>

      {packs.length > 0 && (
        <div data-testid="purchase-history-packages">
          <div className="label-mono text-[#A3A3A3] mb-1.5">Packages</div>
          <div className="space-y-2">
            {(expanded ? packs : packs.slice(0, 3)).map((p) => {
              const dead = p.status !== "active" || !p.paid || p.remaining === 0;
              const isDup = !!p.duplicate_of;
              return (
                <div
                  key={p.id}
                  data-testid={`purchase-history-pack-${p.id}`}
                  className="bg-[#0A0A0A] border p-3 space-y-2"
                  style={{ borderColor: isDup ? "#FF9F1C" : (dead ? "#262626" : "#CCFF00") }}
                >
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold truncate flex items-center gap-2 flex-wrap">
                        {p.name}
                        {isDup && (
                          <span
                            className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-[#FF9F1C] text-[#FF9F1C]"
                            data-testid={`duplicate-badge-${p.id}`}
                            title={`Likely duplicate of ${p.duplicate_of} — same Stripe session or bought within 60s of another pack for the same offering`}
                          >
                            <AlertTriangle className="w-3 h-3" /> Possible duplicate
                          </span>
                        )}
                        {p.status === "refunded" && (
                          <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-[#FF4500] text-[#FF4500]">
                            Refunded
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-[#A3A3A3] uppercase tracking-wider">
                        Bought {fmtDate(p.created_at)}
                        {p.expires_at ? ` · Expires ${fmtDate(p.expires_at)}` : ""}
                        {p.status !== "active" ? ` · ${p.status}` : ""}
                        {p.price ? ` · £${p.price}` : ""}
                      </div>
                      {isAdmin && (
                        <div className="text-[10px] mt-1 space-y-0.5" data-testid={`pack-provenance-${p.id}`}>
                          {p.stripe_session_id ? (
                            <div className="text-[#66C2FF]">
                              Stripe session: <span className="font-mono">{p.stripe_session_id.slice(0, 24)}…</span>
                            </div>
                          ) : (
                            <div className="text-[#FF9F1C] inline-flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" /> No Stripe charge on this pack
                            </div>
                          )}
                          {p.source_offering_id && (
                            <div className="text-[#525252]">
                              Offering: <span className="font-mono">{p.source_offering_id.slice(0, 24)}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-lg h-display" style={{ color: dead ? "#525252" : "#CCFF00" }}>
                        {p.remaining}/{p.sessions_total}
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">
                        credits left
                      </div>
                    </div>
                  </div>
                  {isAdmin && (
                    <div className="flex gap-2 flex-wrap pt-1 border-t border-[#141414]" data-testid={`admin-pack-actions-${p.id}`}>
                      <button
                        type="button"
                        onClick={() => adjustCredits(p)}
                        disabled={busy === p.id}
                        data-testid={`admin-adjust-${p.id}`}
                        className="px-2.5 py-1 text-[10px] uppercase tracking-wider border border-[#66C2FF] text-[#66C2FF] hover:bg-[#66C2FF] hover:text-black disabled:opacity-40 inline-flex items-center gap-1"
                      >
                        <Wrench className="w-3 h-3" /> Adjust credits
                      </button>
                      {p.status !== "refunded" && (
                        <button
                          type="button"
                          onClick={() => refundPack(p)}
                          disabled={busy === p.id}
                          data-testid={`admin-refund-${p.id}`}
                          className="px-2.5 py-1 text-[10px] uppercase tracking-wider border border-[#FF4500] text-[#FF4500] hover:bg-[#FF4500] hover:text-black disabled:opacity-40 inline-flex items-center gap-1"
                        >
                          <Undo2 className="w-3 h-3" /> Refund
                        </button>
                      )}
                      {/* Iter170 — Delete orphan packs (no Stripe charge + never used).
                          Backend refuses the delete if either condition breaks so the
                          audit trail stays intact for real purchases. */}
                      {!p.stripe_session_id && p.sessions_used === 0 && (
                        <button
                          type="button"
                          onClick={async () => {
                            const note = prompt("Confirm delete this orphan pack (no Stripe charge, never used). Optional reason?", "") ?? null;
                            if (note === null) return;
                            setBusy(p.id);
                            try {
                              await api.delete(`/admin/packages/${p.id}`);
                              toast.success("Orphan pack deleted");
                              load();
                            } catch (e) {
                              toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
                            } finally { setBusy(""); }
                          }}
                          disabled={busy === p.id}
                          data-testid={`admin-delete-${p.id}`}
                          className="px-2.5 py-1 text-[10px] uppercase tracking-wider border border-[#525252] text-[#525252] hover:border-white hover:text-white disabled:opacity-40"
                        >
                          Delete orphan
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {!expanded && packs.length > 3 && (
            <div className="text-[10px] text-[#525252] uppercase tracking-wider mt-1.5">
              +{packs.length - 3} more · tap Expand all
            </div>
          )}
        </div>
      )}

      {usage.length > 0 && (
        <div data-testid="purchase-history-usage">
          <div className="label-mono text-[#A3A3A3] mb-1.5">Sessions redeemed via credit</div>
          <div className="space-y-1.5">
            {(expanded ? usage : usage.slice(0, 5)).map((u) => (
              <div
                key={u.booking_id}
                data-testid={`purchase-history-usage-${u.booking_id}`}
                className="flex items-center justify-between gap-3 text-xs border-b border-[#141414] pb-1.5"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <CalendarClock className="w-3 h-3 text-[#A3A3A3]" />
                  <span className="truncate">{u.title}</span>
                </div>
                <span className="text-[#A3A3A3] shrink-0">{fmtDate(u.start)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {feedback.length > 0 && (
        <div data-testid="purchase-history-feedback">
          <div className="label-mono text-[#A3A3A3] mb-1.5">
            <Sparkles className="w-3.5 h-3.5 inline-block mr-1 text-[#CCFF00]" />
            Session feedback recaps
          </div>
          <div className="space-y-2">
            {(expanded ? feedback : feedback.slice(0, 3)).map((f) => (
              <div
                key={f.id}
                data-testid={`purchase-history-feedback-${f.id}`}
                className="bg-[#0A0A0A] border border-[#262626] p-3 space-y-1"
              >
                <div className="flex items-baseline justify-between gap-2 flex-wrap">
                  <div className="text-sm font-semibold">{f.session_title || "Session"}</div>
                  <div className="text-[10px] text-[#A3A3A3] uppercase tracking-wider">
                    {fmtDate(f.session_start || f.created_at)}
                    {f.coach_name ? ` · ${f.coach_name}` : ""}
                  </div>
                </div>
                {f.ai_summary && (
                  <p className="text-xs text-[#E5E5E5] italic border-l-2 border-[#CCFF00] pl-2">{f.ai_summary}</p>
                )}
                {(f.focus_areas_for_improvement || []).length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {f.focus_areas_for_improvement.slice(0, 4).map((fa) => (
                      <span key={fa} className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-[#FF9F1C] text-[#FF9F1C]">
                        {fa}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
